import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import QRCode from 'qrcode';
import {
  formatFirstSessionDate,
  formatWorkshopSchedule,
  loadWorkshop2026Context,
  WORKSHOP_2026_PATH,
} from '../services/workshop2026Service';
import { Meeting } from '../services/meetingService';
import { WORKSHOP_FLYER_PUBLIC_URL } from '../constants/workshopFlyer';
import {
  WORKSHOP_FLYER_FAQS,
  WORKSHOP_FLYER_INTRO,
  WORKSHOP_FLYER_WHY,
} from '../data/workshopFlyerContent';
import WorkshopFlyerBullets from '../components/workshop2026/WorkshopFlyerBullets';
import { downloadWorkshopFlyerPdf, openWorkshopFlyerPdfForPrint } from '../utils/workshopFlyerPdf';
import styles from '../styles/workshopFlyerPage.module.css';

const WorkshopFlyerPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const flyerRef = useRef<HTMLElement | null>(null);
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const autoPrintHandled = useRef(false);

  useEffect(() => {
    document.body.classList.add('workshop-flyer-route');
    return () => {
      document.body.classList.remove('workshop-flyer-route');
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const ctx = await loadWorkshop2026Context();
      if (!cancelled) {
        setMeeting(ctx.meeting);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    QRCode.toDataURL(WORKSHOP_FLYER_PUBLIC_URL, {
      width: 280,
      margin: 1,
      color: { dark: '#1a252f', light: '#ffffff' },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => {
        if (!cancelled) setQrDataUrl('');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const firstSession = formatFirstSessionDate(meeting);
  const weeklySchedule = formatWorkshopSchedule(meeting);
  const displayUrl = WORKSHOP_FLYER_PUBLIC_URL.replace(/^https:\/\//, '');

  const handleDownloadPdf = useCallback(async () => {
    if (!flyerRef.current || pdfBusy) return;
    setPdfBusy(true);
    try {
      await downloadWorkshopFlyerPdf(flyerRef.current);
    } finally {
      setPdfBusy(false);
    }
  }, [pdfBusy]);

  const handlePrintPdf = useCallback(async () => {
    if (!flyerRef.current || pdfBusy) return;
    setPdfBusy(true);
    try {
      await openWorkshopFlyerPdfForPrint(flyerRef.current);
    } finally {
      setPdfBusy(false);
    }
  }, [pdfBusy]);

  useEffect(() => {
    if (
      searchParams.get('print') !== '1' ||
      loading ||
      !qrDataUrl ||
      pdfBusy ||
      autoPrintHandled.current ||
      !flyerRef.current
    ) {
      return;
    }
    autoPrintHandled.current = true;
    void handlePrintPdf();
  }, [searchParams, loading, qrDataUrl, pdfBusy, handlePrintPdf]);

  const handleBack = useCallback(() => {
    const opener = window.opener;
    if (opener && !opener.closed) {
      try {
        opener.focus();
      } catch {
        /* ignore cross-origin focus errors */
      }
    }
    window.close();
    window.setTimeout(() => {
      navigate(WORKSHOP_2026_PATH);
    }, 150);
  }, [navigate]);

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <button
          type="button"
          className={styles.printBtn}
          onClick={() => void handleDownloadPdf()}
          disabled={loading || !qrDataUrl || pdfBusy}
        >
          {pdfBusy ? 'Preparing…' : 'Download PDF'}
        </button>
        <button
          type="button"
          className={styles.secondaryToolbarBtn}
          onClick={() => void handlePrintPdf()}
          disabled={loading || !qrDataUrl || pdfBusy}
        >
          Open PDF to print
        </button>
        <button type="button" className={styles.toolbarLinkBtn} onClick={handleBack}>
          Back to 2026 Step Workshop
        </button>
      </div>

      {loading ? (
        <p className={styles.loading}>Loading flyer…</p>
      ) : (
        <article
          ref={flyerRef}
          className={styles.flyer}
          aria-label="2026 SAA Step Workshop invitation flyer"
        >
          <div className={styles.flyerHeader}>
            <div className={styles.brandRow}>
              <img src="/recovery_commons_icon.png" alt="" className={styles.logo} />
              <span className={styles.brandName}>Recovery Commons</span>
            </div>
            <h1 className={styles.workshopTitle}>2026 SAA Step Workshop</h1>
            <p className={styles.inviteLead}>You&apos;re invited to join us</p>
          </div>

          <div className={styles.flyerBody}>
            <div className={styles.startBlock}>
              <p className={styles.startLabel}>First session</p>
              <p className={styles.startDate}>{firstSession}</p>
              <p className={styles.startSchedule}>Then weekly · {weeklySchedule}</p>
            </div>

            <p className={styles.introText}>{WORKSHOP_FLYER_INTRO}</p>

            <section className={styles.flyerSection}>
              <h2 className={styles.flyerSectionTitle}>Why a step workshop?</h2>
              <WorkshopFlyerBullets items={WORKSHOP_FLYER_WHY} />
            </section>

            <section className={styles.flyerSection}>
              <h2 className={styles.flyerSectionTitle}>Good to know</h2>
              <WorkshopFlyerBullets items={WORKSHOP_FLYER_FAQS} className={styles.flyerFaqGrid} />
            </section>
          </div>

          <div className={styles.ctaBlock}>
            <div className={styles.ctaText}>
              <p className={styles.ctaHeading}>Learn more &amp; register on Recovery Commons</p>
              <a
                href={WORKSHOP_FLYER_PUBLIC_URL}
                className={styles.ctaUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {displayUrl}
              </a>
              <p className={styles.ctaHint}>Full details, FAQs, and registration on the 2026 Step Workshop page.</p>
            </div>
            {qrDataUrl && (
              <div className={styles.qrWrap}>
                <img src={qrDataUrl} alt="QR code for recoverycommons.com/2026-workshop" className={styles.qrImage} />
                <p className={styles.qrCaption}>Scan to learn more</p>
              </div>
            )}
          </div>
        </article>
      )}
    </div>
  );
};

export default WorkshopFlyerPage;
